import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiRequest } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import type { LeadLabelPayload, LeadLabelRecord } from '../types/leads';

interface LeadLabelsResponse {
  leads: LeadLabelRecord[];
}

interface LeadLabelMutationResponse {
  lead: LeadLabelRecord;
}

const QUERY_KEY = ['lead-labels'];

export function useLeadLabels(includeArchived = false, enabled = true) {
  const { session } = useAuth();
  const accessToken = session?.access_token;

  return useQuery<LeadLabelRecord[]>({
    queryKey: [...QUERY_KEY, includeArchived ? 'all' : 'active'],
    enabled,
    queryFn: async () => {
      const params = includeArchived ? '?includeArchived=true' : '';
      const data = await apiRequest<LeadLabelsResponse>(
        `/api/leads${params}`,
        { method: 'GET' },
        accessToken,
      );
      return data?.leads ?? [];
    },
  });
}

export function useLeadLabelMutation() {
  const { session } = useAuth();
  const queryClient = useQueryClient();
  const accessToken = session?.access_token;

  return useMutation({
    mutationFn: async (payload: LeadLabelPayload) => {
      const response = await apiRequest<LeadLabelMutationResponse>(
        '/api/leads',
        {
          method: 'PATCH',
          body: JSON.stringify(payload),
        },
        accessToken,
      );
      return response.lead;
    },
    onSuccess: () => {
      // Invalidate all lead-labels queries (active and archived)
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });
}

export function useArchiveLead() {
  const mutation = useLeadLabelMutation();

  return useMutation({
    mutationFn: async (jid: string) => {
      return mutation.mutateAsync({ jid, archived: true });
    },
  });
}

export function useUnarchiveLead() {
  const mutation = useLeadLabelMutation();

  return useMutation({
    mutationFn: async (jid: string) => {
      return mutation.mutateAsync({ jid, archived: false });
    },
  });
}

export function useLeadLabelMap(includeArchived = false, enabled = true) {
  const query = useLeadLabels(includeArchived, enabled);
  const map = useMemo(() => {
    const leadMap = new Map<string, LeadLabelRecord>();
    query.data?.forEach((lead) => {
      leadMap.set(lead.jid, lead);
    });
    return leadMap;
  }, [query.data]);

  return { ...query, map };
}
